import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Table,
  Spinner,
  Card,
  Form,
  Button,
  Pagination,
} from "react-bootstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalLocations, setTotalLocations] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;

  const API_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("authToken");

  const sumTotalLocations = (data) => {
    const total = data.filter((customer) => customer.BranchCode).length;
    setTotalLocations(total);
  };

  useEffect(() => {
    axios
      .get(`${API_URL}/api/customers`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setCustomers(response.data);
        const uniqueCount = new Set(
          response.data.map((c) => c.CusID).filter(Boolean),
        ).size;
        setTotalCustomers(uniqueCount);
        sumTotalLocations(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setError("Failed to fetch data");
        setLoading(false);
      });
  }, [token, API_URL]);

  const filteredCustomers = customers
    .filter((customer) => {
      const code = String(customer.RegedCustomerCode || "").toUpperCase();
      if (searchTerm && !code.includes(searchTerm)) return false;
      if (startDate || endDate) {
        const createdDate = customer.CreatedAt
          ? new Date(customer.CreatedAt).toISOString().split("T")[0]
          : null;
        if (!createdDate) return false;
        if (startDate && createdDate < startDate) return false;
        if (endDate && createdDate > endDate) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      let valA, valB;
      if (sortField === "#") {
        valA = Number(a.CusID) || 0;
        valB = Number(b.CusID) || 0;
        return sortDir === "asc" ? valA - valB : valB - valA;
      }
      if (sortField === "code") {
        valA = String(a.RegedCustomerCode || "");
        valB = String(b.RegedCustomerCode || "");
        return sortDir === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      if (sortField === "date") {
        valA = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
        valB = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
        return sortDir === "asc" ? valA - valB : valB - valA;
      }
      return 0;
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortIcon = (field) => {
    if (sortField !== field) return " ↕";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  const handleExportToExcel = () => {
    const dataToExport = filteredCustomers.map((customer) => ({
      "Customer Code": customer.RegedCustomerCode,
      "Branch Code": customer.BranchCode,
      Mobile: customer.RegedMobile,
      "Joined Date": customer.CreatedAt
        ? new Date(customer.CreatedAt).toISOString().split("T")[0]
        : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(dataBlob, "Customers.xlsx");
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredCustomers.length / PAGE_SIZE);
  const pagedCustomers = filteredCustomers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="page">
        <h1>Customers</h1>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <h1>Customers</h1>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="page">
      <h1>Customers</h1>
      <p>Manage your Customers here.</p>
      <Card className="mb-4">
        <Card.Body>
          <h5>Total Customers: {totalCustomers}</h5>
        </Card.Body>
      </Card>
      <Card className="mb-4">
        <Card.Body>
          <h5>Total Locations: {totalLocations}</h5>
        </Card.Body>
      </Card>

      {/* Search and Date Filters */}
      <Form className="mb-3 d-flex flex-wrap gap-3 align-items-center">
        <Form.Control
          type="text"
          placeholder="Search by Customer Code"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value.toUpperCase());
            setCurrentPage(1);
          }}
          style={{ flex: "2" }}
          size="sm"
        />
        <Form.Control
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setCurrentPage(1);
          }}
          style={{ flex: "1" }}
          size="sm"
        />
        <Form.Control
          type="date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setCurrentPage(1);
          }}
          style={{ flex: "1" }}
          size="sm"
        />
        <Button
          variant="outline-secondary"
          onClick={handleClearFilters}
          size="sm"
        >
          Clear Filters
        </Button>
      </Form>
      {/* Export Button */}
      <div className="mb-3 text-end">
        <Button variant="success" onClick={handleExportToExcel} size="sm">
          Download Excel
        </Button>
      </div>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th
              onClick={() => handleSort("#")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              ID {sortIcon("#")}
            </th>
            <th>Customer Code</th>
            <th>Branch Code</th>
            <th>Mobile</th>
            <th
              onClick={() => handleSort("date")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Joined Date{sortIcon("date")}
            </th>
          </tr>
        </thead>
        <tbody>
          {pagedCustomers.map((customer) => (
            <tr key={`${customer.CusID}-${customer.BranchCode}`}>
              <td>{customer.CusID}</td>
              <td>{customer.RegedCustomerCode}</td>
              <td>{customer.BranchCode}</td>
              <td>{customer.RegedMobile}</td>
              <td>
                {customer.CreatedAt
                  ? new Date(customer.CreatedAt).toISOString().split("T")[0]
                  : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            Showing {(currentPage - 1) * PAGE_SIZE + 1}–
            {Math.min(currentPage * PAGE_SIZE, filteredCustomers.length)} of{" "}
            {filteredCustomers.length} records
          </small>
          <Pagination size="sm" className="mb-0">
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) =>
                  p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2,
              )
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) {
                  acc.push(<Pagination.Ellipsis key={`e${p}`} disabled />);
                }
                acc.push(
                  <Pagination.Item
                    key={p}
                    active={p === currentPage}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </Pagination.Item>,
                );
                return acc;
              }, [])}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default Customers;
